import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';

const { 
  FiPlus, FiChevronLeft, FiEdit2, FiEdit3, FiTrash2, FiVideo, FiFileText, 
  FiMoreVertical, FiCheckCircle, FiMic, FiMonitor, FiAirplay, 
  FiFile, FiTarget, FiLock, FiLink, FiGlobe, FiSettings, FiLayers,
  FiBookOpen, FiHelpCircle, FiChevronDown, FiChevronUp, FiEye,
  FiImage, FiDollarSign, FiActivity
} = FiIcons;

const CONTENT_TYPES = [
  { id: 'video', label: 'Video Lesson', icon: FiVideo, description: 'YouTube, Vimeo, or MP4 links' },
  { id: 'audio', label: 'Audio / Podcast', icon: FiMic, description: 'MP3 or hosted audio files' },
  { id: 'pdf', label: 'PDF Document', icon: FiFile, description: 'E-books, guides, or worksheets' },
  { id: 'ppt', label: 'Slide Deck', icon: FiMonitor, description: 'PowerPoint or Google Slides' },
  { id: 'live', label: 'Live Session', icon: FiAirplay, description: 'Zoom, Meet, or Webinar links' },
  { id: 'text', label: 'Rich Text', icon: FiFileText, description: 'Articles and written guides' },
  { id: 'quiz', label: 'Assessment', icon: FiHelpCircle, description: 'Quizzes and exams' },
];

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [prerequisites, setPrerequisites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('curriculum');
  const [expandedModules, setExpandedModules] = useState({});

  // CRUD States
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'module' | 'lesson', id: string }
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [lessonType, setLessonType] = useState('video');
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [id]);

  const fetchCourseData = async () => {
    try {
      const [{ data: courseData }, { data: moduleData }, { data: preReqData }, { data: coursesData }] = await Promise.all([
        supabase.from('courses_2024').select('*').eq('id', id).single(),
        supabase.from('modules_2024').select(`*, lessons:lessons_2024(*)`).eq('course_id', id).order('order_index', { ascending: true }),
        supabase.from('course_prerequisites_2024').select('prerequisite_id').eq('course_id', id),
        supabase.from('courses_2024').select('id, title').neq('id', id)
      ]);
      
      setCourse(courseData);
      setModules(moduleData || []);
      setPrerequisites(preReqData?.map(p => p.prerequisite_id) || []);
      setAllCourses(coursesData || []);
      
      // Auto-expand first module
      if (moduleData?.[0]) setExpandedModules({ [moduleData[0].id]: true });
    } catch (error) {
      addToast(error.message, 'error');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    const updates = Object.fromEntries(formData.entries());
    try {
      const { error } = await supabase.from('courses_2024').update(updates).eq('id', id);
      if (error) throw error;
      setCourse({ ...course, ...updates });
      addToast("Course updated successfully!");
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const title = new FormData(e.target).get('title');
    try {
      if (editingModule) {
        const { error } = await supabase.from('modules_2024').update({ title }).eq('id', editingModule.id);
        if (error) throw error;
        setModules(modules.map(m => m.id === editingModule.id ? { ...m, title } : m));
        addToast("Module updated!");
      } else {
        const { data, error } = await supabase.from('modules_2024').insert([{ title, course_id: id, order_index: modules.length }]).select();
        if (error) throw error;
        setModules([...modules, { ...data[0], lessons: [] }]);
        setExpandedModules(prev => ({ ...prev, [data[0].id]: true }));
        addToast("Module added!");
      }
      setIsModuleModalOpen(false);
      setEditingModule(null);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleLessonSubmit = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    const payload = {
      title: formData.get('title'),
      module_id: selectedModuleId,
      content_type: lessonType,
      video_url: formData.get('video_url'),
      external_url: formData.get('external_url'),
      file_url: formData.get('file_url'),
      body_content: formData.get('body_content'),
    };

    try {
      if (editingLesson) {
        const { error } = await supabase.from('lessons_2024').update(payload).eq('id', editingLesson.id);
        if (error) throw error;
        setModules(modules.map(m => m.id === selectedModuleId ? {
          ...m,
          lessons: m.lessons.map(l => l.id === editingLesson.id ? { ...l, ...payload } : l)
        } : m));
        addToast("Lesson updated!");
      } else {
        payload.order_index = modules.find(m => m.id === selectedModuleId).lessons.length;
        const { data, error } = await supabase.from('lessons_2024').insert([payload]).select();
        if (error) throw error;
        setModules(modules.map(m => m.id === selectedModuleId ? { ...m, lessons: [...m.lessons, data[0]] } : m));
        addToast("Lesson added!");
      }
      setIsLessonModalOpen(false);
      setEditingLesson(null);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async () => {
    setModalLoading(true);
    try {
      if (deleteTarget.type === 'module') {
        await supabase.from('modules_2024').delete().eq('id', deleteTarget.id);
        setModules(modules.filter(m => m.id !== deleteTarget.id));
      } else {
        await supabase.from('lessons_2024').delete().eq('id', deleteTarget.id);
        setModules(modules.map(m => ({ ...m, lessons: m.lessons.filter(l => l.id !== deleteTarget.id) })));
      }
      setIsDeleteModalOpen(false);
      addToast(`${deleteTarget.type === 'module' ? 'Module' : 'Lesson'} deleted`);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const togglePrerequisite = async (targetId) => {
    const isAdding = !prerequisites.includes(targetId);
    try {
      if (isAdding) {
        await supabase.from('course_prerequisites_2024').insert([{ course_id: id, prerequisite_id: targetId }]);
        setPrerequisites([...prerequisites, targetId]);
      } else {
        await supabase.from('course_prerequisites_2024').delete().eq('course_id', id).eq('prerequisite_id', targetId);
        setPrerequisites(prerequisites.filter(p => p !== targetId));
      }
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  if (loading) return (
    <div className="h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
        <p className="text-slate-400 font-mono text-[10px] uppercase tracking-[0.3em] animate-pulse">Initializing Architect...</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-32">
      {/* Premium Header */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-[2.5rem] blur opacity-20 group-hover:opacity-30 transition duration-1000"></div>
        <div className="relative flex justify-between items-center bg-slate-900/40 backdrop-blur-3xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden">
          <div className="flex items-center gap-6">
            <Link to="/courses" className="w-12 h-12 flex items-center justify-center bg-white/5 hover:bg-indigo-600 text-slate-400 hover:text-white rounded-2xl transition-all border border-white/5 hover:border-indigo-400">
              <SafeIcon icon={FiChevronLeft} className="text-xl" />
            </Link>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-black text-white tracking-tighter uppercase leading-none">{course?.title}</h1>
                <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${course?.status === 'Published' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {course?.status}
                </span>
              </div>
              <div className="flex gap-6 mt-3">
                {[
                  { id: 'curriculum', label: 'Curriculum', icon: FiLayers },
                  { id: 'settings', label: 'Architect Settings', icon: FiSettings },
                ].map(tab => (
                  <button 
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 text-[10px] uppercase font-black tracking-widest transition-all ${activeTab === tab.id ? 'text-indigo-400 border-b-2 border-indigo-400 pb-1' : 'text-slate-500 hover:text-slate-300'}`}
                  >
                    <SafeIcon icon={tab.icon} /> {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button onClick={() => navigate(`/courses/${id}`)} className="bg-white/5 hover:bg-white/10 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-white/10 transition-all flex items-center gap-2">
              <SafeIcon icon={FiEye} /> Preview
            </button>
            <button onClick={() => { setEditingModule(null); setIsModuleModalOpen(true); }} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-600/20 transition-all flex items-center gap-2">
              <SafeIcon icon={FiPlus} /> New Module
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'curriculum' ? (
          <motion.div 
            key="curriculum" 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -20 }} 
            className="space-y-6"
          >
            {modules.length === 0 ? (
              <div className="bg-white/[0.02] border border-white/5 rounded-[2.5rem] p-20 text-center space-y-6">
                <div className="w-20 h-20 bg-indigo-500/10 text-indigo-400 rounded-[2rem] flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiBookOpen} className="text-4xl" />
                </div>
                <h3 className="text-2xl font-black text-white uppercase tracking-tight">Empty Curriculum</h3>
                <p className="text-slate-500 max-w-sm mx-auto">Your course architecture is empty. Start by creating your first learning module.</p>
                <button onClick={() => setIsModuleModalOpen(true)} className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest">
                  Create First Module
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {modules.map((module, mIdx) => (
                  <motion.div 
                    layout
                    key={module.id} 
                    className="bg-white/[0.02] border border-white/5 rounded-[2rem] overflow-hidden backdrop-blur-xl group"
                  >
                    <div className="p-6 flex justify-between items-center bg-white/[0.02] border-b border-white/5">
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => toggleModule(module.id)}
                          className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-black text-xs hover:scale-110 transition-transform"
                        >
                          {mIdx + 1}
                        </button>
                        <div>
                          <h3 className="text-lg font-black text-white uppercase tracking-tight group-hover:text-indigo-400 transition-colors">{module.title}</h3>
                          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{module.lessons.length} Lessons • Module {mIdx + 1}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => { setSelectedModuleId(module.id); setIsLessonModalOpen(true); setEditingLesson(null); }} 
                          className="px-4 py-2 bg-white/5 hover:bg-indigo-600 text-slate-400 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2"
                        >
                          <SafeIcon icon={FiPlus} /> Add Content
                        </button>
                        <button onClick={() => { setEditingModule(module); setIsModuleModalOpen(true); }} className="p-2 text-slate-500 hover:text-white transition-colors"><SafeIcon icon={FiEdit2} /></button>
                        <button onClick={() => { setDeleteTarget({ type: 'module', id: module.id }); setIsDeleteModalOpen(true); }} className="p-2 text-slate-500 hover:text-rose-400 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                        <button onClick={() => toggleModule(module.id)} className="p-2 text-slate-500 hover:text-white transition-all transform group-hover:scale-110">
                          <SafeIcon icon={expandedModules[module.id] ? FiChevronUp : FiChevronDown} />
                        </button>
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {expandedModules[module.id] && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 space-y-2">
                            {module.lessons.map((lesson, lIdx) => (
                              <div 
                                key={lesson.id} 
                                className="flex items-center gap-4 p-4 hover:bg-white/[0.04] rounded-2xl transition-all group/lesson border border-transparent hover:border-white/5"
                              >
                                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-500 group-hover/lesson:text-indigo-400 transition-colors">
                                  <SafeIcon icon={CONTENT_TYPES.find(t => t.id === lesson.content_type)?.icon || FiFileText} />
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-slate-200 group-hover/lesson:text-white">{lesson.title}</p>
                                  <p className="text-[9px] text-slate-500 uppercase tracking-widest mt-0.5 font-black">{lesson.content_type}</p>
                                </div>
                                <div className="opacity-0 group-hover/lesson:opacity-100 flex gap-1 transition-opacity">
                                  <button 
                                    onClick={() => { 
                                      setSelectedModuleId(module.id); 
                                      setEditingLesson(lesson); 
                                      setLessonType(lesson.content_type);
                                      setIsLessonModalOpen(true); 
                                    }} 
                                    className="p-2 bg-white/5 rounded-lg text-slate-400 hover:text-white transition-colors"
                                  >
                                    <SafeIcon icon={FiEdit2} className="text-xs" />
                                  </button>
                                  <button 
                                    onClick={() => { setDeleteTarget({ type: 'lesson', id: lesson.id }); setIsDeleteModalOpen(true); }} 
                                    className="p-2 bg-white/5 rounded-lg text-slate-400 hover:text-rose-400 transition-colors"
                                  >
                                    <SafeIcon icon={FiTrash2} className="text-xs" />
                                  </button>
                                </div>
                              </div>
                            ))}
                            {module.lessons.length === 0 && (
                              <div className="py-10 text-center">
                                <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">No lessons in this module</p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div 
            key="settings" 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -20 }} 
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Main Settings */}
            <div className="lg:col-span-2 space-y-8">
              <form onSubmit={handleUpdateCourse} className="bg-white/[0.02] border border-white/5 rounded-[2.5rem] p-10 space-y-8 backdrop-blur-xl">
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center">
                    <SafeIcon icon={FiEdit3} className="text-xl" />
                  </div>
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">Core Details</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Course Title</label>
                    <input 
                      name="title" 
                      defaultValue={course?.title}
                      className="w-full bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-white font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</label>
                    <select 
                      name="status" 
                      defaultValue={course?.status}
                      className="w-full bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-white font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all appearance-none"
                    >
                      <option value="Draft">Draft</option>
                      <option value="Published">Published</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Description</label>
                    <textarea 
                      name="description" 
                      defaultValue={course?.description}
                      className="w-full bg-white/5 border border-white/5 rounded-2xl px-6 py-4 text-white font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all h-32 resize-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Base Price ($)</label>
                    <div className="relative">
                      <SafeIcon icon={FiDollarSign} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input 
                        name="price" 
                        type="number"
                        defaultValue={course?.price}
                        className="w-full bg-white/5 border border-white/5 rounded-2xl pl-12 pr-6 py-4 text-white font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Thumbnail URL</label>
                    <div className="relative">
                      <SafeIcon icon={FiImage} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input 
                        name="image_url" 
                        defaultValue={course?.image_url}
                        className="w-full bg-white/5 border border-white/5 rounded-2xl pl-12 pr-6 py-4 text-white font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" 
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button 
                    disabled={modalLoading}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-indigo-600/20 transition-all disabled:opacity-50"
                  >
                    {modalLoading ? 'Saving...' : 'Update Architect'}
                  </button>
                </div>
              </form>
            </div>

            {/* Side Settings */}
            <div className="space-y-8">
              <div className="bg-white/[0.02] border border-white/5 rounded-[2.5rem] p-8 space-y-6 backdrop-blur-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center">
                    <SafeIcon icon={FiLayers} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white uppercase tracking-tight">Prerequisites</h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Unlock dependencies</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {allCourses.map(c => (
                    <button
                      key={c.id}
                      onClick={() => togglePrerequisite(c.id)}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all text-left group ${prerequisites.includes(c.id) ? 'bg-indigo-600/10 border-indigo-600/50' : 'bg-white/5 border-transparent hover:bg-white/10'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg transition-colors ${prerequisites.includes(c.id) ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-500 group-hover:text-slate-300'}`}>
                          <SafeIcon icon={FiBookOpen} className="text-xs" />
                        </div>
                        <span className={`text-[10px] font-black uppercase tracking-tight ${prerequisites.includes(c.id) ? 'text-white' : 'text-slate-500'}`}>{c.title}</span>
                      </div>
                      {prerequisites.includes(c.id) && <SafeIcon icon={FiCheckCircle} className="text-indigo-400" />}
                    </button>
                  ))}
                  {allCourses.length === 0 && (
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest text-center py-4">No other courses available</p>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border border-white/10 rounded-[2.5rem] p-8 space-y-4">
                 <SafeIcon icon={FiActivity} className="text-3xl text-indigo-400" />
                 <h4 className="text-sm font-black text-white uppercase tracking-tight">Analytics Sync</h4>
                 <p className="text-[11px] text-slate-400 leading-relaxed font-medium">Changes to the curriculum will automatically recalibrate progress metrics for all {course?.students_count || 0} enrolled students.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <FormModal 
        isOpen={isModuleModalOpen} 
        onClose={() => setIsModuleModalOpen(false)} 
        title={editingModule ? "Edit Module" : "New Module"} 
        onSubmit={handleModuleSubmit} 
        loading={modalLoading}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Module Title</label>
            <input 
              name="title" 
              required 
              defaultValue={editingModule?.title}
              className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-white font-bold focus:ring-2 focus:ring-indigo-500 outline-none" 
              placeholder="e.g. Fundamental Concepts" 
            />
          </div>
        </div>
      </FormModal>

      <FormModal 
        isOpen={isLessonModalOpen} 
        onClose={() => setIsLessonModalOpen(false)} 
        title={editingLesson ? "Edit Lesson" : "Add New Content"} 
        onSubmit={handleLessonSubmit} 
        loading={modalLoading}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Content Title</label>
            <input 
              name="title" 
              required 
              defaultValue={editingLesson?.title}
              className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-white font-bold outline-none focus:ring-2 focus:ring-indigo-500" 
              placeholder="Enter name" 
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Content Format</label>
            <div className="grid grid-cols-4 gap-2">
              {CONTENT_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setLessonType(type.id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${lessonType === type.id ? 'bg-indigo-600/20 border-indigo-600 text-indigo-400' : 'bg-slate-800 border-transparent text-slate-500 hover:bg-slate-700'}`}
                >
                  <SafeIcon icon={type.icon} className="text-lg" />
                  <span className="text-[8px] font-black uppercase tracking-tighter">{type.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 bg-slate-800 rounded-2xl">
            {lessonType === 'video' ? (
              <input name="video_url" defaultValue={editingLesson?.video_url} required className="w-full bg-slate-900 border-none rounded-xl px-4 py-3 text-white text-sm font-medium" placeholder="YouTube or MP4 URL" />
            ) : lessonType === 'text' ? (
              <textarea name="body_content" defaultValue={editingLesson?.body_content} required className="w-full bg-slate-900 border-none rounded-xl px-4 py-3 text-white text-sm font-medium h-32 resize-none" placeholder="Write content..." />
            ) : (
              <input name="file_url" defaultValue={editingLesson?.file_url} required className="w-full bg-slate-900 border-none rounded-xl px-4 py-3 text-white text-sm font-medium" placeholder="Enter external link or file URL" />
            )}
          </div>
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)} 
        onConfirm={handleDelete} 
        loading={modalLoading}
        title={`Delete ${deleteTarget?.type === 'module' ? 'Module' : 'Lesson'}?`}
      >
        <p className="text-slate-400 text-sm">Are you sure you want to remove this? This action cannot be undone and will delete all associated content.</p>
      </ConfirmModal>

      {/* Floating Save Indicator */}
      <motion.div 
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 px-8 py-4 bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-full shadow-2xl flex items-center gap-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Architect Online</span>
        </div>
        <div className="h-4 w-px bg-white/10" />
        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Autosaving to Cloud...</p>
      </motion.div>
    </div>
  );
}